import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  PROJECT_FILE_KIND_PLURALS,
  ProjectFileKind,
  ProjectFileSort,
  type ProjectFileKind as Kind,
  type ProjectFileSort as Sort,
  type ProjectFileView,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { EmptyState } from '../ui/EmptyState.js';
import { SearchField } from '../ui/SearchField.js';
import { SegmentedFilter, type FilterOption } from '../ui/SegmentedFilter.js';
import { navigatorKeyDown } from '../ui/SplitWorkspace.js';
import { errorText, useDebounced } from '../references/hooks.js';
import { FileCard, FileSkeletons } from './FileCard.js';
import type { FilesAddress, FilesAddressChange } from './address.js';
import { fileShareUrl, kindCounts, matchesSearch, SORT_LABELS, sortFiles } from './model.js';

type ChipId = 'all' | Kind;

/** "PDFs", "spreadsheets" — a kind's plural inside a sentence. */
function inSentence(kind: Kind): string {
  const plural = PROJECT_FILE_KIND_PLURALS[kind];
  return kind === 'PDF' ? plural : plural.toLowerCase();
}

/**
 * THE LIST ITSELF — search, the kind chips (All and each kind this production
 * holds, with its count), sort, rows or cards. Every one of those is the
 * address's, so a reload shows the same list.
 *
 * Three different "nothing here"s, because each sends somebody somewhere else: no
 * files at all (the page's empty state, drawn by the caller), a search that matched
 * nothing, and a kind with nothing in it.
 */
export function FilesLibrary({
  projectId,
  files,
  loading,
  error,
  onRetry,
  address,
  update,
  empty,
  onNavigate,
}: {
  projectId: string;
  files: readonly ProjectFileView[] | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  address: FilesAddress;
  update: (change: FilesAddressChange, options?: { push?: boolean }) => void;
  /** What an empty production shows instead of the toolbar and the list. */
  empty: ReactNode;
  /** How a file is being opened: explicitly (focus goes to it) or by the arrow keys (focus stays). */
  onNavigate: (via: 'open' | 'arrow') => void;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const sortId = useId();

  // The search box types freely; the address follows once it settles.
  const [draft, setDraft] = useState(address.q);
  const settled = useDebounced(draft, 250);
  const written = useRef(address.q);
  useEffect(() => {
    if (settled.trim() === written.current.trim()) return;
    written.current = settled;
    update({ q: settled });
  }, [settled, update]);
  useEffect(() => {
    if (address.q.trim() === written.current.trim()) return;
    written.current = address.q;
    setDraft(address.q);
  }, [address.q]);

  const all = files ?? [];
  const counts = useMemo(() => kindCounts(all), [all]);
  const shown = useMemo(
    () =>
      sortFiles(
        all.filter((f) => (address.kind === null || f.kind === address.kind) && matchesSearch(f, address.q)),
        address.sort,
      ),
    [all, address.kind, address.q, address.sort],
  );

  const present = ProjectFileKind.options.filter((k) => (counts[k] ?? 0) > 0 || address.kind === k);
  const chips: FilterOption<ChipId>[] = [
    { id: 'all', label: 'All', ...(files ? { count: all.length } : {}) },
    ...present.map((k) => ({ id: k, label: PROJECT_FILE_KIND_PLURALS[k], ...(files ? { count: counts[k] ?? 0 } : {}) })),
  ];

  const copy = async (file: ProjectFileView) => {
    const url = fileShareUrl(projectId, file.id);
    try {
      await navigator.clipboard.writeText(url);
      setStatus('Link copied.');
    } catch {
      setStatus(`Copy this link: ${url}`);
    }
  };

  if (files !== undefined && files.length === 0 && !loading) {
    return (
      <div className="files-library-inner" data-testid="files-library">
        {empty}
      </div>
    );
  }

  const narrowed = address.q.trim() !== '' || address.kind !== null;

  return (
    <div className="files-library-inner" data-testid="files-library">
      <div className="ref-toolbar">
        <div className="ref-toolbar-row">
          <div className="ref-search">
            <SearchField
              value={draft}
              onChange={setDraft}
              placeholder="Search by file name or who uploaded it"
              label="Search files"
              testId="files-search"
            />
          </div>
          <div className="ref-toolbar-end">
            <label className="ref-sort" htmlFor={sortId}>
              <span>Sort</span>
              <select
                id={sortId}
                className="ref-select"
                value={address.sort}
                data-testid="files-sort"
                onChange={(event) => {
                  update({ sort: event.target.value as Sort });
                }}
              >
                {ProjectFileSort.options.map((s) => (
                  <option key={s} value={s}>
                    {SORT_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
            <div className="ref-view-toggle" role="group" aria-label="View">
              <button
                type="button"
                className="ref-view-btn"
                aria-pressed={address.view === 'list'}
                aria-label="Show as a list"
                title="List"
                data-testid="files-view-list"
                onClick={() => {
                  update({ view: 'list' });
                }}
              >
                <Icon name="list" size={16} />
              </button>
              <button
                type="button"
                className="ref-view-btn"
                aria-pressed={address.view === 'grid'}
                aria-label="Show as cards"
                title="Cards"
                data-testid="files-view-grid"
                onClick={() => {
                  update({ view: 'grid' });
                }}
              >
                <Icon name="grid" size={16} />
              </button>
            </div>
          </div>
        </div>
        <SegmentedFilter
          options={chips}
          value={address.kind ?? 'all'}
          onChange={(id) => {
            update({ kind: id === 'all' ? null : id });
          }}
          label="Show"
          testIdPrefix="files-filter"
        />
      </div>

      <p className="sr-only" role="status" aria-live="polite" data-testid="files-results-status">
        {files === undefined ? '' : `${String(shown.length)} file${shown.length === 1 ? '' : 's'}`}
      </p>
      {status !== null ? (
        <p className="ref-quiet" role="status" data-testid="files-library-status">
          {status}
        </p>
      ) : null}

      {loading ? (
        <FileSkeletons view={address.view} />
      ) : error !== null && files === undefined ? (
        <EmptyState
          testId="files-error"
          title="The files could not be loaded"
          action={
            <button type="button" className="ctl" data-testid="files-retry" onClick={onRetry}>
              Try again
            </button>
          }
        >
          {errorText(error, 'Nothing is lost — this is a problem reaching the server.')}
        </EmptyState>
      ) : shown.length === 0 && narrowed ? (
        address.q.trim() !== '' ? (
          <EmptyState
            testId="files-empty-search"
            title={`Nothing matches “${address.q.trim()}”`}
            action={
              <button
                type="button"
                className="ctl"
                data-testid="files-empty-search-clear"
                onClick={() => {
                  setDraft('');
                  written.current = '';
                  update({ q: null });
                }}
              >
                Clear the search
              </button>
            }
          >
            {address.kind === null
              ? 'The search looks at file names and at who uploaded each file.'
              : `The search looks at file names and at who uploaded each file, among ${inSentence(address.kind)} only.`}
          </EmptyState>
        ) : (
          <EmptyState
            testId="files-empty-filter"
            title={`No ${address.kind === null ? 'files' : inSentence(address.kind)} in this production`}
            action={
              <button
                type="button"
                className="ctl"
                data-testid="files-empty-filter-clear"
                onClick={() => {
                  update({ kind: null });
                }}
              >
                Show all files
              </button>
            }
          />
        )
      ) : (
        <>
          <h3 className="sr-only">Files</h3>
          <ul
            className="files-results"
            data-view={address.view}
            data-testid="files-results"
            onKeyDown={navigatorKeyDown(
              shown.map((f) => f.id),
              address.file,
              (id) => {
                // Moving through the list keeps the focus in the list; only an
                // explicit open hands it to the file.
                onNavigate('arrow');
                update({ file: id });
              },
              (id) => `[data-file-row="${id}"]`,
            )}
          >
            {shown.map((file) => (
              <li key={file.id}>
                <FileCard
                  file={file}
                  view={address.view}
                  selected={address.file === file.id}
                  onOpen={() => {
                    onNavigate('open');
                    update({ file: file.id }, { push: address.file === null });
                  }}
                  menu={[
                    {
                      id: 'open',
                      label: 'Open',
                      testId: `files-item-menu-${file.id}-open`,
                      onSelect: () => {
                        onNavigate('open');
                        update({ file: file.id }, { push: address.file === null });
                      },
                    },
                    {
                      id: 'copy',
                      label: 'Copy link',
                      testId: `files-item-menu-${file.id}-copy`,
                      onSelect: () => {
                        void copy(file);
                      },
                    },
                  ]}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
